import { useEffect, useRef, useState } from 'react'
import { useFunctions } from 'reactfire'
import { api } from '../../../lib/api'
import { logger } from '../../../lib/logger'
import { hasSignature } from '../replySignature'

interface OperatorSignatureResponse {
  signature?: string | null
}

export type OperatorSignatureStatus = 'loading' | 'loaded' | 'failed'

interface OperatorSignature {
  signature: string | null
  status: OperatorSignatureStatus
}

export function useChatwootOperatorSignature(
  companyUid?: string | null
): OperatorSignature {
  const functions = useFunctions()
  // Held in a ref and kept out of the dep array: useFunctions() is not guaranteed
  // to return a stable reference, and an unstable dep here means every state
  // update restarts the effect, which restarts the update. That loop hangs the
  // composer.
  const functionsRef = useRef(functions)
  functionsRef.current = functions
  const [signature, setSignature] = useState<string | null>(null)
  const [status, setStatus] = useState<OperatorSignatureStatus>('loading')
  const trimmedCompanyUid = companyUid?.trim() || undefined

  useEffect(() => {
    let cancelled = false

    api(functionsRef.current, 'chatwoot/get_operator_signature', {
      companyUid: trimmedCompanyUid,
    })
      .then((raw) => {
        if (cancelled) {
          return
        }
        const response = raw as OperatorSignatureResponse | undefined
        setSignature(
          hasSignature(response?.signature)
            ? (response?.signature as string)
            : null
        )
        setStatus('loaded')
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        logger.warn('Chatwoot operator signature fetch failed', {
          eventType: 'operator_signature_fetch_failed',
        })
        setSignature(null)
        setStatus('failed')
      })

    return () => {
      cancelled = true
    }
  }, [functions, trimmedCompanyUid])

  return { signature, status }
}
